import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import Compressor from 'compressorjs';
import { v4 as uuidv4 } from 'uuid';
import { PreparedUploadInterface, UploadPreprocessCategory } from '../interfaces/prepared-upload.interface';
import { blobToBase64 } from '../../utils/encoding/encoding';
import { VideoTranscoder } from '../plugins/video-transcoder.plugin';
import { UploadTaskTrackerService } from './upload-task-tracker.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UploadPreprocessService {
  private readonly imagePreprocessThresholdBytes = 1.5 * 1024 * 1024;
  private readonly imageMaxDimension = 2560;
  private readonly imageQuality = 0.9;
  private readonly videoPreprocessThresholdBytes = 50 * 1024 * 1024;
  private readonly videoNearBackendCapBytes = 180_000_000;
  private readonly videoTargetLongEdge = 1920;
  private readonly videoSoftTargetLongEdge = 2048;
  private readonly videoReductionFactor = 0.8;
  private readonly videoStrongReductionFactor = 0.7;
  private readonly videoSoftReductionFactor = 1;
  private readonly videoMinBitrate = 2_000_000;
  private readonly videoMaxBitrate = 10_000_000;
  private readonly videoSoftMinBitrate = 12_000_000;
  private readonly videoSoftMaxBitrate = 34_000_000;
  private readonly veryLargeVideoThresholdBytes = 80 * 1024 * 1024;
  private readonly videoMinEffectiveReductionFactor = 0.95;
  private readonly videoPreviewAndUploadFrameRate = 60;
  private readonly backendMaxVideoUploadBytes = 500 * 1024 * 1024;
  private readonly backendSafeVideoUploadBytes = 498 * 1024 * 1024;
  private readonly maxAllowedDurationDriftSeconds = 0.35;
  private readonly webTranscodeTimeoutMs = 180_000;
  private ffmpegReadyPromise: Promise<{
    ffmpeg: {
      load: (config?: {
        classWorkerURL?: string;
        coreURL?: string;
        wasmURL?: string;
      }) => Promise<void>;
      loaded: boolean;
      writeFile: (path: string, data: Uint8Array) => Promise<void>;
      exec: (args: string[], timeout?: number) => Promise<number>;
      readFile: (path: string) => Promise<Uint8Array>;
      deleteFile: (path: string) => Promise<void>;
      terminate?: () => void;
    };
    fetchFile: (file: File) => Promise<Uint8Array>;
    loadFfmpeg: () => Promise<void>;
  }> | null = null;
  private ffmpegExecutionQueue: Promise<void> = Promise.resolve();
  private ffmpegLogTail: string[] = [];
  private readonly preparedVideoUploadCache = new WeakMap<File, PreparedUploadInterface>();
  private preprocessCancelGeneration = 0;

  constructor(
    private readonly uploadTaskTracker: UploadTaskTrackerService,
  ) {}

  public async preprocessForPreview(
    file: File,
    category: UploadPreprocessCategory
  ): Promise<{ file: File; preprocessed: boolean; reason: string }> {
    if (category === 'image' && !this.isImagePreprocessEnabled()) {
      return { file, preprocessed: false, reason: 'passthrough:image_preprocess_disabled_by_env' };
    }

    if (category === 'video' && !this.isVideoPreprocessEnabled()) {
      return { file, preprocessed: false, reason: 'passthrough:video_preprocess_disabled_by_env' };
    }

    if (category === 'image' && this.isBrowserEnvironment() && await this.shouldPreprocessImage(file)) {
      try {
        const compressed = await this.compressImage(file);
        const effective = compressed.size < file.size * this.videoMinEffectiveReductionFactor;
        if (effective) {
          return { file: compressed, preprocessed: true, reason: 'image:preview_compressed' };
        }
        return { file, preprocessed: false, reason: 'passthrough:image_preview_not_effective' };
      } catch (error) {
        console.warn('[UploadPreprocess] image preview preprocess failed, fallback to original', error);
        return { file, preprocessed: false, reason: 'passthrough:image_preview_error' };
      }
    }

    if (category === 'video') {
      // Keep original cadence for preview (avoid FPS degradation in UI preview).
      return {
        file,
        preprocessed: false,
        reason: 'passthrough:preview_video_original',
      };
    }

    return { file, preprocessed: false, reason: 'passthrough:preview_not_applicable' };
  }

  public async prepare(file: File, category: UploadPreprocessCategory, flow: string): Promise<PreparedUploadInterface> {
    const cancelGeneration = this.preprocessCancelGeneration;
    if (category === 'video') {
      const cachedPreparedUpload = this.preparedVideoUploadCache.get(file);
      if (cachedPreparedUpload && cachedPreparedUpload.flow === flow && cachedPreparedUpload.category === category) {
        this.uploadTaskTracker.setPreprocessReason(cachedPreparedUpload.clientUploadId, cachedPreparedUpload.preprocessReason);
        this.uploadTaskTracker.markPendingUpload(cachedPreparedUpload.clientUploadId, cachedPreparedUpload.sizeAfterBytes);
        this.logPreparedUpload(cachedPreparedUpload);
        return cachedPreparedUpload;
      }
    }

    const clientUploadId = uuidv4();
    const sizeBeforeBytes = file.size;
    console.info('[UploadPreprocess] prepare start', {
      clientUploadId,
      flow,
      category,
      fileType: file.type,
      sizeBeforeBytes,
      fileName: file.name,
    });

    this.uploadTaskTracker.startTask({
      clientUploadId,
      fileName: file.name,
      mimeType: file.type,
      sizeBeforeBytes,
      flow,
    });

    try {
      let processedFile = file;
      let preprocessReason = 'passthrough:not_applicable';

      if (category === 'image' && !this.isImagePreprocessEnabled()) {
        preprocessReason = 'passthrough:image_preprocess_disabled_by_env';
      } else if (category === 'video' && !this.isVideoPreprocessEnabled()) {
        preprocessReason = 'passthrough:video_preprocess_disabled_by_env';
      } else {
        if (category === 'image' && this.isBrowserEnvironment() && await this.shouldPreprocessImage(file)) {
          processedFile = await this.compressImage(file);
          preprocessReason = processedFile === file ? 'passthrough:image_not_effective' : 'image:compressed';
        } else if (category === 'image') {
          preprocessReason = 'passthrough:image_below_threshold';
        }

        if (category === 'video') {
          const videoResult = await this.preprocessVideo(file, cancelGeneration);
          processedFile = videoResult.file;
          preprocessReason = videoResult.reason;
        }
      }

      this.uploadTaskTracker.setPreprocessReason(clientUploadId, preprocessReason);
      this.uploadTaskTracker.markPendingUpload(clientUploadId, processedFile.size);

      const preparedUpload: PreparedUploadInterface = {
        clientUploadId,
        flow,
        category,
        file: processedFile,
        originalFile: file,
        sizeBeforeBytes,
        sizeAfterBytes: processedFile.size,
        preprocessed: processedFile !== file,
        preprocessReason,
      };

      if (category === 'video') {
        this.preparedVideoUploadCache.set(file, preparedUpload);
      }

      this.logPreparedUpload(preparedUpload);

      return preparedUpload;
    } catch (error) {
      this.uploadTaskTracker.markFailed(clientUploadId, error);
      throw error;
    }
  }

  public cancelActivePreprocess(): void {
    this.preprocessCancelGeneration += 1;
    const activePromise = this.ffmpegReadyPromise;
    this.ffmpegReadyPromise = null;
    this.ffmpegLogTail = [];
    if (!activePromise) {
      return;
    }
    activePromise.then(({ ffmpeg }) => {
      try {
        ffmpeg.terminate?.();
      } catch {
        // noop
      }
    }).catch(() => {
      // noop
    });
  }

  public markUploaded(preparedUpload: PreparedUploadInterface): void {
    this.uploadTaskTracker.markUploaded(preparedUpload.clientUploadId, preparedUpload.sizeAfterBytes);
    this.logUploadState(preparedUpload, 'UPLOADED');
    if (preparedUpload.category === 'video') {
      this.preparedVideoUploadCache.delete(preparedUpload.originalFile);
    }
  }

  public markFailed(preparedUpload: PreparedUploadInterface, error?: unknown): void {
    this.uploadTaskTracker.markFailed(preparedUpload.clientUploadId, error);
    this.logUploadState(preparedUpload, 'FAILED');
  }

  private async shouldPreprocessImage(file: File): Promise<boolean> {
    if (!file.type.startsWith('image/')) {
      return false;
    }

    if (file.size >= this.imagePreprocessThresholdBytes) {
      return true;
    }

    const dimensions = await this.getImageDimensions(file);
    if (!dimensions) {
      return false;
    }

    return dimensions.width > this.imageMaxDimension || dimensions.height > this.imageMaxDimension;
  }

  private async compressImage(file: File): Promise<File> {
    const result = await new Promise<File | Blob>((resolve, reject) => {
      new Compressor(file, {
        quality: this.imageQuality,
        maxWidth: this.imageMaxDimension,
        maxHeight: this.imageMaxDimension,
        strict: true,
        success: (value) => resolve(value),
        error: (err) => reject(err),
      });
    });

    if (result instanceof File) {
      return result;
    }

    return new File([result], file.name, {
      type: result.type || file.type,
      lastModified: Date.now(),
    });
  }

  private async getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    if (!this.isBrowserEnvironment()) {
      return null;
    }

    const imageUrl = URL.createObjectURL(file);
    try {
      const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
          resolve({ width: image.naturalWidth, height: image.naturalHeight });
        };
        image.onerror = reject;
        image.src = imageUrl;
      });

      return dimensions;
    } catch {
      return null;
    } finally {
      URL.revokeObjectURL(imageUrl);
    }
  }

  private isBrowserEnvironment(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  private async preprocessVideo(file: File, cancelGeneration: number): Promise<{ file: File; reason: string }> {
    this.throwIfCancelled(cancelGeneration);
    if (!this.isVideoFile(file)) {
      return { file, reason: 'passthrough:not_video_mime' };
    }

    if (file.size < this.videoPreprocessThresholdBytes) {
      return { file, reason: 'passthrough:video_below_threshold' };
    }

    const transcodeDecision = await this.shouldTranscodeVideoForUpload(file);
    if (transcodeDecision.mode === 'none') {
      return { file, reason: transcodeDecision.reason };
    }
    const transcodeMode: 'soft' | 'hard' = transcodeDecision.mode;

    if (Capacitor.isNativePlatform()) {
      if (!Capacitor.isPluginAvailable('VideoTranscoder')) {
        return { file, reason: 'passthrough:native_plugin_unavailable' };
      }

      try {
        const transcoded = await this.transcodeVideoWithNativePlugin(file, transcodeMode, cancelGeneration);
        if (!transcoded) {
          return { file, reason: 'passthrough:video_not_effective' };
        }
        const isPlayable = await this.isPlayableVideo(transcoded);
        if (!isPlayable) {
          return { file, reason: 'passthrough:transcoded_video_unplayable' };
        }
        if (transcoded.size >= file.size * this.videoMinEffectiveReductionFactor) {
          return { file, reason: 'passthrough:video_not_effective' };
        }
        return { file: transcoded, reason: 'video:native_transcoded_mp4' };
      } catch (error) {
        console.warn('[UploadPreprocess] native video transcode failed, fallback to original', error);
        return { file, reason: 'passthrough:native_video_transcode_error' };
      }
    }

    if (!this.isBrowserEnvironment()) {
      return { file, reason: 'passthrough:non_browser_runtime' };
    }

    try {
      const transcodedByFfmpeg = await this.enqueueFfmpegExecution(
        () => this.transcodeVideoWithFfmpegWasm(file, transcodeMode, cancelGeneration)
      );
      if (!transcodedByFfmpeg) {
        throw new Error('ffmpeg_wasm_transcode_no_output');
      }

      const minEffectiveReductionFactor = transcodeMode === 'soft' ? 0.98 : this.videoMinEffectiveReductionFactor;
      if (transcodedByFfmpeg.size >= file.size * minEffectiveReductionFactor) {
        return { file, reason: 'passthrough:video_transcode_not_effective' };
      }

      const isPlayable = await this.isPlayableVideo(transcodedByFfmpeg);
      if (!isPlayable) {
        throw new Error('ffmpeg_wasm_transcoded_video_unplayable');
      }

      const sourceDuration = await this.getVideoDuration(file);
      const transcodedDuration = await this.getVideoDuration(transcodedByFfmpeg);
      if (!this.hasPreservedDuration(sourceDuration, transcodedDuration)) {
        console.warn('[UploadPreprocess] ffmpeg duration mismatch', {
          sourceDuration,
          transcodedDuration,
        });
        throw new Error('ffmpeg_wasm_duration_mismatch');
      }

      return { file: transcodedByFfmpeg, reason: 'video:ffmpeg_wasm_transcoded' };
    } catch (error) {
      console.warn('[UploadPreprocess] ffmpeg wasm transcode failed', error);
      throw error;
    }
  }

  private async shouldTranscodeVideoForUpload(
    file: File
  ): Promise<{ mode: 'none' | 'soft' | 'hard'; reason: string }> {
    if (file.size >= this.videoNearBackendCapBytes) {
      return { mode: 'soft', reason: 'video:soft_transcode_near_backend_cap' };
    }

    if (file.size >= this.videoPreprocessThresholdBytes) {
      return { mode: 'soft', reason: 'video:soft_transcode_over_50mb' };
    }

    return { mode: 'none', reason: 'passthrough:video_below_threshold' };
  }

  private async transcodeVideoWithFfmpegWasm(file: File, mode: 'soft' | 'hard', cancelGeneration: number): Promise<File | null> {
    this.throwIfCancelled(cancelGeneration);
    console.info('[UploadPreprocess] ffmpeg transcode start', {
      fileName: file.name,
      fileType: file.type,
      size: file.size,
      mode,
    });
    const { ffmpeg, fetchFile, loadFfmpeg } = await this.ensureFfmpegReady();
    this.throwIfCancelled(cancelGeneration);
    if (!ffmpeg.loaded) {
      await loadFfmpeg();
      this.throwIfCancelled(cancelGeneration);
    }
    const inputExtension = this.getVideoExtension(file.type);
    const inputPath = `input-${uuidv4()}.${inputExtension}`;
    const outputPath = `output-${uuidv4()}.mp4`;
    const enforceHardCap = mode === 'hard';
    const shouldTryRemux = mode === 'soft' && file.type !== 'video/quicktime';

    try {
      console.info('[UploadPreprocess] ffmpeg writeFile');
      await ffmpeg.writeFile(inputPath, await fetchFile(file));
      this.throwIfCancelled(cancelGeneration);

      if (shouldTryRemux) {
        // Fast path: remux when codecs are already compatible (keeps full quality, very fast).
        const remuxCode = await ffmpeg.exec([
          '-i', inputPath,
          '-c', 'copy',
          '-movflags', '+faststart',
          '-y',
          outputPath,
        ], 45_000);

        if (remuxCode === 0) {
          console.info('[UploadPreprocess] ffmpeg remux success');
          const remuxData = await ffmpeg.readFile(outputPath);
          if (remuxData?.length) {
            const remuxBlob = new Blob([remuxData], { type: 'video/mp4' });
            const remuxFile = new File([remuxBlob], this.renameWithExtension(file.name, 'mp4'), {
              type: 'video/mp4',
              lastModified: Date.now(),
            });
            if (remuxFile.size <= file.size * 0.97) {
              this.throwIfCancelled(cancelGeneration);
              return remuxFile;
            }
          }
        }
      } else {
        console.info('[UploadPreprocess] remux skipped', {
          enforceHardCap,
          fileType: file.type,
        });
      }

      const sourceBitrate = await this.estimateVideoBitrate(file);
      const durationSeconds = Math.max(1, await this.getVideoDuration(file));
      const sourceDimensions = await this.getVideoDimensions(file);
      this.throwIfCancelled(cancelGeneration);
      const reductionFactor = enforceHardCap
        ? (file.size >= this.veryLargeVideoThresholdBytes ? this.videoStrongReductionFactor : this.videoReductionFactor)
        : this.videoSoftReductionFactor;
      const targetFromSource = Math.round(sourceBitrate * reductionFactor);
      const audioBitrate = 96_000;
      const capDrivenVideoBitrate = Math.round(((this.backendSafeVideoUploadBytes * 8) / durationSeconds) - audioBitrate);
      const targetVideoBitrate = enforceHardCap
        ? Math.min(targetFromSource, capDrivenVideoBitrate)
        : targetFromSource;

      const sourceLongEdge = sourceDimensions ? Math.max(sourceDimensions.width, sourceDimensions.height) : 0;

      if (!enforceHardCap && sourceLongEdge > 0 && sourceLongEdge <= this.videoSoftTargetLongEdge) {
        // Fast path for soft mode: keep video stream untouched, only normalize audio.
        const fastSoftCode = await ffmpeg.exec([
          '-i', inputPath,
          '-map', '0:v:0',
          '-map', '0:a:0?',
          '-dn',
          '-sn',
          '-map_metadata', '-1',
          '-c:v', 'copy',
          '-c:a', 'aac',
          '-b:a', '96k',
          '-ac', '2',
          '-ar', '48000',
          '-movflags', '+faststart',
          '-y',
          outputPath,
        ], 45_000);

        if (fastSoftCode === 0) {
          const fastSoftData = await ffmpeg.readFile(outputPath);
          if (fastSoftData?.length) {
            this.throwIfCancelled(cancelGeneration);
            const fastSoftBlob = new Blob([fastSoftData], { type: 'video/mp4' });
            return new File([fastSoftBlob], this.renameWithExtension(file.name, 'mp4'), {
              type: 'video/mp4',
              lastModified: Date.now(),
            });
          }
        }
      }

      const encodePass = async (maxLongEdge: number, bitrate: number, passLabel: string): Promise<File | null> => {
        this.throwIfCancelled(cancelGeneration);
        const boundedBitrate = enforceHardCap
          ? this.clamp(bitrate, 450_000, this.videoMaxBitrate)
          : this.clamp(bitrate, this.videoSoftMinBitrate, this.videoSoftMaxBitrate);
        const videoBitrateK = Math.max(350, Math.round(boundedBitrate / 1000));
        const scaleFilter = this.resolveScaleFilter(sourceDimensions, maxLongEdge);
        const encodingProfiles: Array<{ label: string; args: string[] }> = enforceHardCap ? [
          {
            label: 'libx264+aac',
            args: [
              '-c:v', 'libx264',
              '-preset', 'ultrafast',
              '-pix_fmt', 'yuv420p',
              '-c:a', 'aac',
              '-b:a', enforceHardCap ? '96k' : '128k',
              '-ac', '2',
              '-ar', '48000',
            ],
          },
          {
            label: 'mpeg4+aac',
            args: [
              '-c:v', 'mpeg4',
              '-c:a', 'aac',
              '-b:a', enforceHardCap ? '96k' : '128k',
              '-ac', '2',
              '-ar', '48000',
            ],
          },
        ] : [
          {
            label: 'libx264+aac',
            args: [
              '-c:v', 'libx264',
              '-preset', 'ultrafast',
              '-pix_fmt', 'yuv420p',
              '-c:a', 'aac',
              '-b:a', '96k',
              '-ac', '2',
              '-ar', '48000',
            ],
          },
        ];

        for (const profile of encodingProfiles) {
          console.info('[UploadPreprocess] ffmpeg exec start', {
            passLabel,
            profile: profile.label,
            maxLongEdge,
            videoBitrateK,
          });

          const resultCode = await ffmpeg.exec([
            '-i', inputPath,
            '-map', '0:v:0',
            '-map', '0:a:0?',
            '-dn',
            '-sn',
            '-map_metadata', '-1',
            '-vf', scaleFilter,
            ...profile.args,
            '-b:v', `${videoBitrateK}k`,
            '-movflags', '+faststart',
            '-y',
            outputPath,
          ]);

          if (resultCode !== 0) {
            const ffmpegLogText = this.ffmpegLogTail.slice(-40).join('\n');
            console.warn('[UploadPreprocess] ffmpeg exec failed', {
              passLabel,
              profile: profile.label,
              resultCode,
              ffmpegLogTail: this.ffmpegLogTail.slice(-20),
              ffmpegLogText,
            });
            continue;
          }

          const data = await ffmpeg.readFile(outputPath);
          if (!data?.length) {
            console.warn('[UploadPreprocess] ffmpeg empty output', { passLabel, profile: profile.label });
            continue;
          }

          this.throwIfCancelled(cancelGeneration);
          const blob = new Blob([data], { type: 'video/mp4' });
          return new File([blob], this.renameWithExtension(file.name, 'mp4'), {
            type: 'video/mp4',
            lastModified: Date.now(),
          });
        }

        return null;
      };

      const pass1LongEdge = enforceHardCap ? this.videoTargetLongEdge : this.videoSoftTargetLongEdge;
      const pass1 = await encodePass(pass1LongEdge, targetVideoBitrate, 'pass1');
      this.throwIfCancelled(cancelGeneration);
      if (!pass1) {
        return null;
      }
      if (!enforceHardCap) {
        return pass1;
      }
      if (pass1.size <= this.backendSafeVideoUploadBytes) {
        return pass1;
      }

      const pass2 = await encodePass(1280, Math.round(targetVideoBitrate * 0.7), 'pass2');
      this.throwIfCancelled(cancelGeneration);
      if (!pass2) {
        return null;
      }
      return pass2;
    } finally {
      try {
        await ffmpeg.deleteFile(inputPath);
      } catch {
        // noop
      }
      try {
        await ffmpeg.deleteFile(outputPath);
      } catch {
        // noop
      }
    }
  }

  private async ensureFfmpegReady(): Promise<{
    ffmpeg: {
      load: (config?: {
        classWorkerURL?: string;
        coreURL?: string;
        wasmURL?: string;
      }) => Promise<void>;
      loaded: boolean;
      writeFile: (path: string, data: Uint8Array) => Promise<void>;
      exec: (args: string[], timeout?: number) => Promise<number>;
      readFile: (path: string) => Promise<Uint8Array>;
      deleteFile: (path: string) => Promise<void>;
      terminate?: () => void;
    };
    fetchFile: (file: File) => Promise<Uint8Array>;
    loadFfmpeg: () => Promise<void>;
  }> {
    if (!this.ffmpegReadyPromise) {
      this.ffmpegReadyPromise = (async () => {
        const [{ FFmpeg }, { fetchFile }] = await Promise.all([
          import('@ffmpeg/ffmpeg'),
          import('@ffmpeg/util'),
        ]);

        const ffmpeg = new FFmpeg();
        const classWorkerURL = this.resolveAssetUrl('assets/ffmpeg/ffmpeg/worker.js');
        const coreURL = this.resolveAssetUrl('assets/ffmpeg/core/ffmpeg-core.js');
        const wasmURL = this.resolveAssetUrl('assets/ffmpeg/core/ffmpeg-core.wasm');
        const loadFfmpeg = async () => {
          if (ffmpeg.loaded) {
            return;
          }
          await ffmpeg.load({
            classWorkerURL,
            coreURL,
            wasmURL,
          });
        };
        await loadFfmpeg();
        ffmpeg.on('log', ({ message }: { message: string }) => {
          this.ffmpegLogTail.push(message);
          if (this.ffmpegLogTail.length > 200) {
            this.ffmpegLogTail.shift();
          }
        });
        return { ffmpeg: ffmpeg as unknown as {
            load: (config?: {
              classWorkerURL?: string;
              coreURL?: string;
              wasmURL?: string;
            }) => Promise<void>;
            loaded: boolean;
            writeFile: (path: string, data: Uint8Array) => Promise<void>;
            exec: (args: string[], timeout?: number) => Promise<number>;
            readFile: (path: string) => Promise<Uint8Array>;
            deleteFile: (path: string) => Promise<void>;
            terminate?: () => void;
          }, fetchFile: fetchFile as (file: File) => Promise<Uint8Array>, loadFfmpeg };
      })();
    }

    return this.ffmpegReadyPromise;
  }

  private resolveAssetUrl(relativeAssetPath: string): string {
    if (!this.isBrowserEnvironment()) {
      return `/${relativeAssetPath}`;
    }

    const base = document.baseURI || window.location.origin + '/';
    return new URL(relativeAssetPath, base).toString();
  }

  private async transcodeVideoWithNativePlugin(file: File, mode: 'soft' | 'hard', cancelGeneration: number): Promise<File | null> {
    this.throwIfCancelled(cancelGeneration);
    const extension = this.getVideoExtension(file.type);
    const inputPath = `upload-preprocess/video-input-${uuidv4()}.${extension}`;
    const base64 = await blobToBase64(file);

    await Filesystem.writeFile({
      path: inputPath,
      data: base64,
      directory: Directory.Cache,
      recursive: true,
    });

    try {
      const inputUri = await Filesystem.getUri({
        path: inputPath,
        directory: Directory.Cache,
      });

      const sourceBitrate = await this.estimateVideoBitrate(file);
      const sourceDimensions = await this.getVideoDimensions(file);
      this.throwIfCancelled(cancelGeneration);
      const sourceLongEdge = sourceDimensions ? Math.max(sourceDimensions.width, sourceDimensions.height) : 0;
      const maxLongEdge = mode === 'soft'
        ? Math.min(this.videoSoftTargetLongEdge, sourceLongEdge || this.videoSoftTargetLongEdge)
        : this.videoTargetLongEdge;
      const reductionFactor = mode === 'soft'
        ? this.videoSoftReductionFactor
        : this.videoReductionFactor;
      const minBitrate = mode === 'soft' ? this.videoSoftMinBitrate : this.videoMinBitrate;
      const maxBitrate = mode === 'soft' ? this.videoSoftMaxBitrate : this.videoMaxBitrate;
      const targetBitrate = this.clamp(
        Math.round(sourceBitrate * reductionFactor),
        minBitrate,
        maxBitrate
      );

      const transcoded = await VideoTranscoder.transcodeVideo({
        inputPath: inputUri.uri,
        maxLongEdge,
        targetBitrate,
      });

      if (!transcoded?.outputPath) {
        return null;
      }
      this.throwIfCancelled(cancelGeneration);

      const response = await fetch(this.toFetchableUrl(transcoded.outputPath));
      if (!response.ok) {
        throw new Error(`Unable to fetch transcoded output: ${response.status}`);
      }

      const blob = await response.blob();
      this.throwIfCancelled(cancelGeneration);
      return new File([blob], this.renameWithExtension(file.name, 'mp4'), {
        type: 'video/mp4',
        lastModified: Date.now(),
      });
    } finally {
      try {
        await Filesystem.deleteFile({
          path: inputPath,
          directory: Directory.Cache,
        });
      } catch {
        // Ignore cache cleanup errors.
      }
    }
  }

  private resolveRecorderProfiles(): Array<{ mimeType: string; extension: string }> {
    const recorder = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
    if (!recorder?.isTypeSupported) {
      return [];
    }

    // Prefer WebM profiles on web because MP4 support in MediaRecorder is inconsistent.
    const candidates = [
      { mimeType: 'video/webm;codecs=vp9,opus', extension: 'webm' },
      { mimeType: 'video/webm;codecs=vp8,opus', extension: 'webm' },
      { mimeType: 'video/webm', extension: 'webm' },
      { mimeType: 'video/mp4;codecs="avc1.42E01E,mp4a.40.2"', extension: 'mp4' },
      { mimeType: 'video/mp4;codecs=avc1.42E01E,mp4a.40.2', extension: 'mp4' },
      { mimeType: 'video/mp4', extension: 'mp4' },
    ];

    return candidates.filter((candidate) => recorder.isTypeSupported(candidate.mimeType));
  }

  private async transcodeVideoWithMediaRecorder(
    file: File,
    profile: { mimeType: string; extension: string }
  ): Promise<File | null> {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';

    const objectUrl = URL.createObjectURL(file);
    video.src = objectUrl;

    const cleanup = () => {
      video.pause();
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(objectUrl);
    };

    try {
      await this.waitForMetadata(video);
      const duration = Math.max(1, Number.isFinite(video.duration) ? video.duration : 1);

      const sourceStream = this.getSourceCaptureStream(video);
      if (!sourceStream) {
        return null;
      }

      const sourceBitrate = Math.round((file.size * 8) / duration);
      const reductionFactor = file.size >= this.veryLargeVideoThresholdBytes
        ? this.videoStrongReductionFactor
        : this.videoReductionFactor;
      const targetVideoBitrate = this.clamp(
        Math.round(sourceBitrate * reductionFactor),
        this.videoMinBitrate,
        this.videoMaxBitrate
      );

      const chunks: Blob[] = [];
      const recorder = new MediaRecorder(sourceStream, {
        mimeType: profile.mimeType,
        videoBitsPerSecond: targetVideoBitrate,
      });

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.start(1000);
      await video.play();

      await this.waitForVideoEnded(video, duration);

      if (recorder.state !== 'inactive') {
        recorder.stop();
      }
      await this.waitForRecorderStop(recorder);
      sourceStream.getTracks().forEach((track) => track.stop());

      if (!chunks.length) {
        return null;
      }

      const blob = new Blob(chunks, { type: profile.mimeType });
      const outputName = this.renameWithExtension(file.name, profile.extension);
      return new File([blob], outputName, {
        type: profile.mimeType,
        lastModified: Date.now(),
      });
    } finally {
      cleanup();
    }
  }

  private getSourceCaptureStream(video: HTMLVideoElement): MediaStream | null {
    const withCapture = video as HTMLVideoElement & {
      captureStream?: (frameRate?: number) => MediaStream;
      mozCaptureStream?: () => MediaStream;
    };
    return withCapture.captureStream?.(this.videoPreviewAndUploadFrameRate) || withCapture.mozCaptureStream?.() || null;
  }

  private waitForMetadata(video: HTMLVideoElement): Promise<void> {
    return new Promise((resolve, reject) => {
      const onLoaded = () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        video.removeEventListener('error', onError);
        resolve();
      };
      const onError = () => {
        video.removeEventListener('loadedmetadata', onLoaded);
        video.removeEventListener('error', onError);
        reject(new Error('Unable to load video metadata.'));
      };

      video.addEventListener('loadedmetadata', onLoaded);
      video.addEventListener('error', onError);
      video.load();
    });
  }

  private waitForVideoEnded(video: HTMLVideoElement, durationSeconds: number): Promise<void> {
    const timeoutMs = Math.max(15_000, Math.round(durationSeconds * 2_000));
    return new Promise((resolve, reject) => {
      const timer = window.setTimeout(() => {
        cleanup();
        reject(new Error('Video transcode timeout.'));
      }, timeoutMs);

      const onEnded = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error('Video playback failed.'));
      };

      const cleanup = () => {
        window.clearTimeout(timer);
        video.removeEventListener('ended', onEnded);
        video.removeEventListener('error', onError);
      };

      video.addEventListener('ended', onEnded);
      video.addEventListener('error', onError);
    });
  }

  private waitForRecorderStop(recorder: MediaRecorder): Promise<void> {
    if (recorder.state === 'inactive') {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const onStop = () => {
        recorder.removeEventListener('stop', onStop);
        recorder.removeEventListener('error', onError);
        resolve();
      };
      const onError = () => {
        recorder.removeEventListener('stop', onStop);
        recorder.removeEventListener('error', onError);
        reject(new Error('MediaRecorder failed during stop.'));
      };

      recorder.addEventListener('stop', onStop);
      recorder.addEventListener('error', onError);
    });
  }

  private fitWithinLongEdge(width: number, height: number, longEdge: number): { width: number; height: number } {
    const currentLongEdge = Math.max(width, height);
    if (currentLongEdge <= longEdge) {
      return { width, height };
    }

    const scale = longEdge / currentLongEdge;
    return {
      width: Math.max(2, Math.round(width * scale)),
      height: Math.max(2, Math.round(height * scale)),
    };
  }

  private renameWithExtension(fileName: string, extension: string): string {
    const base = fileName.replace(/\.[^.]+$/, '');
    return `${base}.${extension}`;
  }

  private async estimateVideoBitrate(file: File): Promise<number> {
    const duration = Math.max(1, await this.getVideoDuration(file));
    return Math.round((file.size * 8) / duration);
  }

  private async getVideoDuration(file: File): Promise<number> {
    if (!this.isBrowserEnvironment()) {
      return 0;
    }

    const objectUrl = URL.createObjectURL(file);
    try {
      const duration = await new Promise<number>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = async () => {
          const loadedDuration = Number(video.duration) || 0;
          if (Number.isFinite(loadedDuration) && loadedDuration > 0) {
            resolve(loadedDuration);
            return;
          }

          // Some MediaRecorder webm outputs expose Infinity duration in metadata.
          // Force a seek near the end to recover a finite duration when possible.
          try {
            const recovered = await this.recoverInfiniteDuration(video);
            resolve(recovered);
          } catch {
            resolve(0);
          }
        };
        video.onerror = () => reject(new Error('Unable to load video metadata.'));
        video.src = objectUrl;
      });
      return duration || 0;
    } catch {
      return 0;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private async getVideoDimensions(file: File): Promise<{ width: number; height: number } | null> {
    if (!this.isBrowserEnvironment()) {
      return null;
    }

    const objectUrl = URL.createObjectURL(file);
    try {
      return await new Promise<{ width: number; height: number }>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          resolve({
            width: video.videoWidth || 0,
            height: video.videoHeight || 0,
          });
        };
        video.onerror = () => reject(new Error('Unable to load video dimensions.'));
        video.src = objectUrl;
      });
    } catch {
      return null;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private resolveScaleFilter(
    sourceDimensions: { width: number; height: number } | null,
    maxLongEdge: number
  ): string {
    if (!sourceDimensions || !sourceDimensions.width || !sourceDimensions.height) {
      return `scale=${maxLongEdge}:-2`;
    }

    const sourceLongEdge = Math.max(sourceDimensions.width, sourceDimensions.height);
    if (sourceLongEdge <= maxLongEdge) {
      // Keep original resolution when already under target; avoid upscaling blur.
      return 'scale=iw:ih';
    }

    const isLandscape = sourceDimensions.width >= sourceDimensions.height;
    return isLandscape
      ? `scale=${maxLongEdge}:-2`
      : `scale=-2:${maxLongEdge}`;
  }

  private recoverInfiniteDuration(video: HTMLVideoElement): Promise<number> {
    return new Promise((resolve) => {
      const timeout = window.setTimeout(() => cleanup(0), 2500);

      const onTimeUpdate = () => {
        const d = Number(video.duration) || 0;
        if (Number.isFinite(d) && d > 0) {
          cleanup(d);
        } else {
          cleanup(0);
        }
      };

      const onSeeked = () => {
        const d = Number(video.duration) || 0;
        if (Number.isFinite(d) && d > 0) {
          cleanup(d);
        }
      };

      const cleanup = (value: number) => {
        window.clearTimeout(timeout);
        video.removeEventListener('timeupdate', onTimeUpdate);
        video.removeEventListener('seeked', onSeeked);
        resolve(value);
      };

      video.addEventListener('timeupdate', onTimeUpdate, { once: true });
      video.addEventListener('seeked', onSeeked, { once: true });
      try {
        video.currentTime = 24 * 60 * 60; // jump far ahead to force duration discovery
      } catch {
        cleanup(0);
      }
    });
  }

  private hasPreservedDuration(sourceDuration: number, transcodedDuration: number): boolean {
    if (sourceDuration <= 0 || transcodedDuration <= 0) {
      return false;
    }

    return Math.abs(sourceDuration - transcodedDuration) <= this.maxAllowedDurationDriftSeconds;
  }

  private async isPlayableVideo(file: File): Promise<boolean> {
    if (!this.isBrowserEnvironment()) {
      return file.size > 0;
    }

    const objectUrl = URL.createObjectURL(file);
    try {
      const metadata = await new Promise<{ duration: number; width: number; height: number }>((resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        const onLoaded = () => {
          cleanup();
          resolve({
            duration: Number(video.duration) || 0,
            width: video.videoWidth || 0,
            height: video.videoHeight || 0,
          });
        };
        const onError = () => {
          cleanup();
          reject(new Error('Video decode error'));
        };
        const cleanup = () => {
          video.removeEventListener('loadedmetadata', onLoaded);
          video.removeEventListener('error', onError);
        };

        video.addEventListener('loadedmetadata', onLoaded);
        video.addEventListener('error', onError);
        video.src = objectUrl;
        video.load();
      });

      return metadata.duration > 0 && metadata.width > 0 && metadata.height > 0;
    } catch {
      return false;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  private getVideoExtension(mimeType?: string): string {
    if (mimeType === 'video/quicktime') {
      return 'mov';
    }
    if (mimeType === 'video/webm') {
      return 'webm';
    }
    return 'mp4';
  }

  private isVideoFile(file: File): boolean {
    if (file?.type?.startsWith('video/')) {
      return true;
    }

    const lowerName = (file?.name || '').toLowerCase();
    return /\.(mp4|mov|m4v|avi|mkv|webm|3gp|hevc|heif)$/i.test(lowerName);
  }

  private toFetchableUrl(path: string): string {
    if (!path) {
      return path;
    }
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
      return path;
    }

    const converted = Capacitor.convertFileSrc(path);
    if (converted.startsWith('capacitor://localhost/')) {
      return converted.replace('capacitor://localhost/', 'http://localhost/');
    }
    return converted;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

  private throwIfCancelled(cancelGeneration: number): void {
    if (cancelGeneration !== this.preprocessCancelGeneration) {
      throw new Error('upload_preprocess_cancelled');
    }
  }

  private isImagePreprocessEnabled(): boolean {
    return environment.mediaPreprocessImageEnabled;
  }

  private isVideoPreprocessEnabled(): boolean {
    return environment.mediaPreprocessVideoEnabled;
  }

  private logPreparedUpload(preparedUpload: PreparedUploadInterface): void {
    const reduction = this.computeReductionPercent(preparedUpload.sizeBeforeBytes, preparedUpload.sizeAfterBytes);
    console.info('[UploadPreprocess] prepared', {
      clientUploadId: preparedUpload.clientUploadId,
      flow: preparedUpload.flow,
      category: preparedUpload.category,
      preprocessed: preparedUpload.preprocessed,
      reason: preparedUpload.preprocessReason,
      sizeBeforeBytes: preparedUpload.sizeBeforeBytes,
      sizeAfterBytes: preparedUpload.sizeAfterBytes,
      reductionPercent: reduction,
    });
  }

  private logUploadState(preparedUpload: PreparedUploadInterface, state: 'UPLOADED' | 'FAILED'): void {
    const reduction = this.computeReductionPercent(preparedUpload.sizeBeforeBytes, preparedUpload.sizeAfterBytes);
    console.info('[UploadPreprocess] state', {
      clientUploadId: preparedUpload.clientUploadId,
      flow: preparedUpload.flow,
      state,
      reason: preparedUpload.preprocessReason,
      sizeBeforeBytes: preparedUpload.sizeBeforeBytes,
      sizeAfterBytes: preparedUpload.sizeAfterBytes,
      reductionPercent: reduction,
    });
  }

  private computeReductionPercent(sizeBeforeBytes: number, sizeAfterBytes: number): number {
    if (!sizeBeforeBytes) {
      return 0;
    }
    return Math.round(((sizeBeforeBytes - sizeAfterBytes) / sizeBeforeBytes) * 100);
  }

  private enqueueFfmpegExecution<T>(job: () => Promise<T>): Promise<T> {
    const run = this.ffmpegExecutionQueue.then(job);
    this.ffmpegExecutionQueue = run.then(() => undefined, () => undefined);
    return run;
  }
}
